import { Autocomplete, Divider, TextField } from "@mui/material";
import axios from "axios";
import axiosInstance from "clients/api";
import usePermission from "components/complex/permission/hook";
import useStateComplex from "hooks/common/useStateComplex";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form-v7";
import { useTheme } from "@mui/material/styles";
import getStyles from "../style";
import useDeviceType from "../../../../hooks/common/useDeviceType";
import { v4 as uuidv4 } from "uuid";

const SelectWithAddSearch = ({
  createDialog: CreateDialog,
  control,
  setValue,
  setError,
  clearErrors,
  error,
  url,
  name,
  apiDataKey,
  label,
  selectCompany,
  moduleName,
  initialData,
  disabled,
  combineName,
  valueDefault,
  isContactWithCompany,
  getAddressDataCallback,
  customItem,
  recordId,
  isAddress,
  apiNameKey,
  addOriginalData
}) => {
  const isWithAddNewFeature = !!CreateDialog;

  const { isMobile } = useDeviceType();
  const theme = useTheme();
  const styles = getStyles(theme, { isMobile });
  const addPermission = moduleName ? usePermission(moduleName, "Add") : false;
  const [defaultValue, setDefaultValue] = useState(valueDefault);
  const [addNew, setAddNew] = useState(false);
  const [state, setState] = useStateComplex({
    isLoading: false,
    list: customItem ? [...customItem] : []
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownElement = document.getElementById("autocomplete");
      // Check if the click is inside the Autocomplete or its input field
      if (!event.target.contains(dropdownElement)) {
        setIsDropdownOpen(false); // Close the dropdown
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const axiosSourceRef = useRef(null);

  const setTableData = (data) => {
    // console.log('clearing error');
    // clearErrors(name);
    const newData = data?.tableData?.map((c) => ({
      code: c[apiDataKey || name],
      name: combineName ? `${c.name} | ${c.email}` : c.name ? c.name : c[apiNameKey],
      ...(addOriginalData ? { original: c } : {}),
      ...(selectCompany &&
        c?.company?.company_id && {
          company: {
            code: c.company.company_id,
            name: c.company.name
          }
        })
    }));
    setState({
      list: customItem ? [...customItem, ...newData] : [...newData],
      isLoading: false
    });
  };

  const setAddressData = (data) => {
    clearErrors(name);
    const newData = data?.map((address) => ({
      code: uuidv4(),
      name: `${address.addressLabel} ${address.city ? `, ${address.city}` : ""} ${address.stateCode ? `, ${address.stateCode}` : ""}`,
      country: address.countryCode,
      city: address.city,
      state: address.stateCode,
      postal_code: address.postalCode,
      [name]: {
        name: address.addressLabel,
        code: address.addressLabel
      }
    }));
    setState({
      list: customItem ? [...customItem, ...newData] : [...newData],
      isLoading: false
    });
  };

  const loadData = async (searchValue) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (axiosSourceRef.current) {
        axiosSourceRef.current.cancel("Operation canceled due to new request.");
      }

      const source = axios.CancelToken.source();
      axiosSourceRef.current = source;

      setState((prev) => ({
        ...prev,
        isLoading: true
      }));

      try {
        const params = {};
        if (isAddress) {
          params.address = encodeURIComponent(searchValue.trim());
        } else {
          params.search_value = searchValue || initialData;
        }
        const { data } = await axiosInstance.get(url, {
          cancelToken: axiosSourceRef.current.token,
          params
        });
        if (isAddress) {
          setValue(name, { name: searchValue });
          setAddressData(data);
        } else if (data.tableData) {
          setTableData(data);
          if (recordId) return data?.tableData?.[0];
        }
      } catch (err) {
        !isAddress &&
          setError(name, {
            type: `${name}.failed`,
            message: `Failed to load ${label}(s)!`
          });

        setState({
          isLoading: false
        });
      }
    }, 300);
  };

  // On item click
  const onSelect = useCallback(
    (props, data) => {
      if (isAddress) {
        getAddressDataCallback(data);
        const newData = data ? { name: data[name].name, code: data.code } : "";
        return props.onChange(newData);
      }
      if (isWithAddNewFeature && data?.index === 0) {
        // Add new item
        setAddNew(true);
      } else {
        // Select from items list
        const d = { ...data };

        if (selectCompany && d?.company) {
          selectCompany(d.company);
          delete d.company;
        }
        props.onChange(d);
      }
    },
    [isWithAddNewFeature, selectCompany, isAddress]
  );

  // On new item create
  const onItemCreate = useCallback(
    (itemData) => {
      const createdItem = {
        code: itemData.code,
        name: itemData.name
      };
      setState({
        list: [itemData, ...state.list]
      });

      setDefaultValue(itemData);

      // Set current key in form
      setValue(name, createdItem, {
        shouldDirty: true,
        shouldValidate: true
      });

      // Set company if needed
      if (selectCompany && itemData?.company) {
        selectCompany(itemData.company);
      }
    },
    [selectCompany, state?.list]
  );

  useEffect(() => {
    if (initialData) {
      if (initialData?.tableData) {
        setTableData(initialData);
      }
    } else if (!isAddress) {
      !recordId && setTimeout(loadData, 0);
    }
  }, [initialData]);

  useEffect(() => {
    if (recordId) {
      loadData(recordId)
        .then((data) => {
          // Handle the loaded data here if needed
          const recordData = {
            name: data.name,
            code: recordId
          };
          setValue(name, recordData);
        })
        .catch((error) => {
          // Handle any errors that occur during data loading
          console.error("Error loading data:", error);
        });
    }
  }, []);

  useEffect(
    () => () => {
      setDefaultValue(null);
    },
    []
  );

  return (
    <div id="autocomplete">
      <Controller
        key={defaultValue}
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field }) => (
          <Autocomplete
            {...field}
            size="small"
            sx={styles.autoCompleteRoot}
            options={
              isWithAddNewFeature && addPermission
                ? [
                    {
                      index: 0,
                      code: "add-new",
                      name: `+ Add New ${label}`
                    },
                    {
                      index: 1,
                      code: "divider",
                      name: "divider",
                      disabled: true
                    },
                    ...state.list
                  ]
                : state.list
            }
            loading={state.isLoading}
            loadingText="Loading..."
            popupIcon={isAddress && null}
            noOptionsText={isAddress && "Start typing to search address…"}
            // disableClearable
            disabled={disabled}
            filterOptions={(options) => options}
            // isOptionEqualToValue={(option, value) => option.code === value}
            getOptionLabel={(option) => option.name || ""}
            renderOption={({ key, ...optionProps }, option, { selected }) =>
              option.code === "divider" ? (
                <li key="divider">
                  <Divider />
                </li>
              ) : (
                <li {...optionProps} key={option.code}>
                    {isContactWithCompany && option?.company?.name ? `${option.name} | ${option.company.name}` : option.name}
                </li>
              )
            }
            // renderOption={option => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="outlined"
                size="small"
                margin="normal"
                sx={{
                  ...styles.field,
                  "& .MuiInputBase-input": styles.input
                }}
                error={Boolean(error)}
                helperText={<span>{error || null}</span>}
                FormHelperTextProps={{
                  sx: styles.errorText
                }}
                open={isDropdownOpen}
                onOpen={() => setIsDropdownOpen(true)}
                onClose={() => setIsDropdownOpen(false)}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password",
                  onClick: (e) => e.stopPropagation()
                }}
                // Loading spinner
                // InputProps={{
                //     ...params.InputProps,
                //     endAdornment: (
                //         <>
                //             {!state.isLoading ? <CircularProgress size={24} thickness={5} style={{ padding: '2px' }}/> : null}
                //             {params.InputProps.endAdornment}
                //         </>
                //     )
                // }}
                form={{
                  autoComplete: "off"
                }}
                InputLabelProps={{
                  sx: {
                    ...styles.label,
                    ...styles.labelNotCapitalize,
                    "&.Mui-focused": styles.labelFocused,
                    "&.MuiInputLabel-shrink": styles.labelShrink
                  }
                }}
                onChange={(e) => loadData(e.target.value)}
                disabled={disabled}
              />
            )}
            onChange={(_, data) => onSelect(field, data)}
            // value="" // TODO -> fix for uncontrolled select
          />
        )}
      />

      {isWithAddNewFeature && addPermission && addNew && (
        <CreateDialog isOpen={addNew} closePopup={() => setAddNew(false)} onItemCreate={onItemCreate} />
      )}
    </div>
  );
};

SelectWithAddSearch.propTypes = {
  createDialog: PropTypes.func,
  control: PropTypes.shape({}).isRequired,
  setValue: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  error: PropTypes.string,
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  moduleName: PropTypes.string,
  disabled: PropTypes.bool,
  selectCompany: PropTypes.func
};

SelectWithAddSearch.defaultProps = {
  selectCompany: undefined,
  createDialog: undefined,
  error: undefined,
  disabled: false
};

export default SelectWithAddSearch;
